import { useMemo, useState } from 'react';
import { SubscriptionSummary } from '../../../../types/subscription';
import { Exchange } from 'grts-utils/types/Exchange';
import { useDistributionState } from './hooks/useDistributionState';
import CardGauges from './CardGauges';
import ConfirmationModal from './ConfirmationModal';
import DropdownComponent from '../../../../components/Form/Dropdown';
import { Dropdown } from '../../../../components/Form';
import './index.css';

interface Props {
  subscriptions: SubscriptionSummary[];
  exchanges: Exchange[];
  selectedExchange: Exchange | null;
  onExchangeChange: (exchange: Exchange) => void;
}

const ManualDistribution = ({
  subscriptions,
  exchanges,
  selectedExchange,
  onExchangeChange,
}: Props) => {
  const {
    entries,
    hasChanges,
    changedEntries,
    setPercentage,
    setTotalBalance,
    setStableBalance,
    setAssetBalance,
    toggleLock,
    reset,
  } = useDistributionState(subscriptions, selectedExchange);

  const [showConfirmation, setShowConfirmation] = useState(false);

  const exchangeDropdownConfig: Dropdown = useMemo(
    () => ({
      label: 'Exchange',
      options: exchanges,
      value: selectedExchange || undefined,
      handleValueChange: (val: string | number) =>
        onExchangeChange(val as Exchange),
      shouldFit: true,
    }),
    [exchanges, selectedExchange, onExchangeChange],
  );

  if (!subscriptions.length) {
    return (
      <div className="manual-distribution">
        <p className="manual-distribution-empty">
          No subscriptions found. Select a cluster with subscriptions to begin.
        </p>
      </div>
    );
  }

  return (
    <div className="manual-distribution">
      <div className="manual-distribution-header">
        <div className="manual-distribution-title-row">
          <h2 className="manual-distribution-title">Manual Distribution</h2>
          {exchanges.length > 1 && (
            <div className="manual-distribution-exchange-dropdown">
              <DropdownComponent config={exchangeDropdownConfig} />
            </div>
          )}
        </div>
        <div className="manual-distribution-actions">
          <button
            className="manual-distribution-btn reset"
            onClick={reset}
            disabled={!hasChanges}
          >
            Reset
          </button>
          <button
            className="manual-distribution-btn submit"
            onClick={() => setShowConfirmation(true)}
            disabled={!hasChanges}
          >
            Submit Changes
          </button>
        </div>
      </div>

      <CardGauges
        entries={entries}
        onPercentageChange={setPercentage}
        onTotalChange={setTotalBalance}
        onStableChange={setStableBalance}
        onAssetChange={setAssetBalance}
        onToggleLock={toggleLock}
      />

      {showConfirmation && (
        <ConfirmationModal
          changedEntries={changedEntries}
          onClose={() => setShowConfirmation(false)}
        />
      )}
    </div>
  );
};

export default ManualDistribution;
